<template>
  <div></div>
</template>

<script>
import { eventBus } from "@/main";
import bridgeService from "@/services/HardwareBridgeService";

export default {
  name: "EDCWebsocket",

  data() {
    return {
      isConnected: false,
      isProcessing: false,
      bridgeUnsub: null,
      timeouts: [],
    };
  },

  created() {
    eventBus.$on("connectWebsocket", this.connectWebSocket);
    eventBus.$on("sendTransaction", this.sendTransaction);
    eventBus.$on("disconnectWebsocket", this.disconnectWebSocket);
    eventBus.$on("cancelTransaction", this.clearAllTimeouts);
  },

  beforeDestroy() {
    this.disconnectWebSocket();
    eventBus.$off("connectWebsocket", this.connectWebSocket);
    eventBus.$off("sendTransaction", this.sendTransaction);
    eventBus.$off("disconnectWebsocket", this.disconnectWebSocket);
    eventBus.$off("cancelTransaction", this.clearAllTimeouts);
  },

  watch: {
    isConnected(val) {
      eventBus.$emit("isEdcWebsocketConnected", val);
    },
  },

  methods: {
    async connectWebSocket() {
      if (this.isConnected) return;
      try {
        await bridgeService.connect();
        this.isConnected = true;
      } catch {
        this.handleError("Cannot connect to Hardware Bridge");
      }
    },

    disconnectWebSocket() {
      if (this.bridgeUnsub) {
        this.bridgeUnsub();
        this.bridgeUnsub = null;
      }
      bridgeService.disconnect();
      this.isConnected = false;
    },

    async sendTransaction({ transactionType, amount, ref1, ref2 }) {
      if (!this.isConnected) {
        await this.connectWebSocket();
      }

      return new Promise((resolve, reject) => {
        if (!this.isConnected) {
          reject(new Error("Failed to establish connection"));
          return;
        }

        this.clearAllTimeouts();
        eventBus.$emit("edcProcessingStatus", true);
        this.isProcessing = true;

        const action =
          transactionType === "card"
            ? "sale"
            : transactionType === "qr"
            ? "qr-sale"
            : null;
        if (!action) {
          reject(new Error("Invalid transaction type"));
          return;
        }

        const message = {
          type: "edc",
          action,
          data: {
            amount,
            ref1: ref1 || "",
            ref2: ref2 || "",
            vatRefund: 0,
          },
        };

        const ackTimeout = setTimeout(() => {
          if (this.bridgeUnsub) this.bridgeUnsub();
          this.isProcessing = false;
          eventBus.$emit("edcProcessingStatus", false);
          reject(new Error("ACK not received within 5 seconds"));
        }, 5000);
        this.timeouts.push(ackTimeout);

        this.bridgeUnsub = bridgeService.send(message, (response) => {
          if (response.event === "ack") {
            clearTimeout(ackTimeout);
            const responseTimeout = setTimeout(() => {
              if (this.bridgeUnsub) this.bridgeUnsub();
              this.isProcessing = false;
              eventBus.$emit("edcProcessingStatus", false);
              reject(new Error("EDC response timeout"));
            }, 60000);
            this.timeouts.push(responseTimeout);
            return;
          }

          // Final response
          this.clearAllTimeouts();
          if (this.bridgeUnsub) this.bridgeUnsub();
          this.isProcessing = false;

          if (response.event === "success") {
            eventBus.$emit("transactionResponse", response.data);
            resolve(response.data);
          } else if (response.event === "error") {
            const err = new Error(response.error?.message || "EDC Error");
            eventBus.$emit("transactionError", err);
            reject(err);
          } else {
            const err = new Error(`Unexpected response: ${JSON.stringify(response)}`);
            eventBus.$emit("transactionError", err);
            reject(err);
          }
        });
      });
    },

    handleError(errorMessage) {
      eventBus.$emit("transactionError", new Error(errorMessage));
      this.isProcessing = false;
      eventBus.$emit("edcProcessingStatus", false);
    },

    clearAllTimeouts() {
      this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
      this.timeouts = [];
      eventBus.$emit("edcProcessingStatus", false);
    },
  },
};
</script>
